import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import Check from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/Info";

import { UIButton } from "../../../../components/ui";
import messages from "../../../../assets/locale/messages";
import {
  getCompanyPortalCanResendActivationCode,
  persistCompanyPortalSession,
} from "../../../../utils/companyRegistration";
import {
  OTP_LENGTH,
  digitsFromPaste,
  digitsOnly,
  formatCountdownMmSs,
  isCompleteOtp,
  resendRegistrationOtp,
  verifyRegistrationOtp,
} from "./otpVerificationLogic";
import { useOtpCountdown } from "./useOtpCountdown";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";

import "./OTPVerification.scss";

const makeBlankOtp = () => "".padEnd(OTP_LENGTH, " ");

const ensureSlots = (otp) =>
  String(otp || "")
    .padEnd(OTP_LENGTH, " ")
    .slice(0, OTP_LENGTH);

const slotChar = (otp, index) => {
  const ch = ensureSlots(otp)[index];
  return ch !== " " ? ch : "";
};

const buildOtpFromDigits = (digitString) => {
  const digits = digitsOnly(digitString).slice(0, OTP_LENGTH);
  const arr = makeBlankOtp().split("");
  for (let i = 0; i < digits.length; i += 1) arr[i] = digits[i];
  return arr.join("");
};

/** Normalize axios `response.data` (object or JSON string) for `message` / `error`. */
const parseAxiosErrorBody = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === "object" && parsed !== null ? parsed : { message: raw };
    } catch {
      return { message: raw };
    }
  }
  return null;
};

/**
 * User-facing message for failed OTP verification (axios error + body shapes).
 */
const resolveOtpVerificationErrorMessage = (err, t, shared) => {
  const status = err?.response?.status;
  const data = parseAxiosErrorBody(err?.response?.data);
  let msg = data?.message ?? data?.error ?? err?.message ?? "";
  if (!msg && Array.isArray(data?.errors)) {
    msg = data.errors
      .map((e) => e?.detail || e?.title || e?.message)
      .filter(Boolean)
      .join(", ");
  }
  if (Array.isArray(msg)) {
    msg = msg.filter(Boolean).join(", ");
  }
  if (typeof msg !== "string") {
    msg = String(msg);
  }
  const trimmed = msg.trim();
  const hasReadableServerMsg =
    trimmed.length > 0 &&
    trimmed.length <= 500 &&
    !/stack trace/i.test(trimmed);

  if (hasReadableServerMsg) return trimmed;
  if (status === 422 || status === 400 || status === 401) return t.errors.incorrect;
  return shared.generalErrMsg;
};

/**
 * OTP verification — registration flow (AuthLayout). Formik holds a fixed-length
 * string (spaces = empty cells); submit uses digit-only value.
 */
const OTPVerification = ({ onNext, onSubmit }) => {
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const { registrationFlow, shared } = messages[lang];
  const t = registrationFlow.otp;
  const completeWith = onSubmit ?? onNext;

  const [canResendActivationCode, setCanResendActivationCode] = useState(() =>
    getCompanyPortalCanResendActivationCode(),
  );

  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    setCanResendActivationCode(getCompanyPortalCanResendActivationCode());
  }, [login]);

  const inputRefs = useRef(null);
  if (!inputRefs.current) {
    inputRefs.current = Array.from({ length: OTP_LENGTH }, () =>
      React.createRef(),
    );
  }

  const { secondsLeft, reset: resetTimer, isCooldownOver } = useOtpCountdown(
    60,
    canResendActivationCode,
  );

  const showResendCooldown = useMemo(
    () => canResendActivationCode && secondsLeft > 0,
    [canResendActivationCode, secondsLeft],
  );

  const resendDisabled = useMemo(
    () =>
      !canResendActivationCode || !isCooldownOver || isResending,
    [canResendActivationCode, isCooldownOver, isResending],
  );

  const countdownLabel = useMemo(
    () => t.afterTimer.replace("{time}", formatCountdownMmSs(secondsLeft)),
    [t.afterTimer, secondsLeft],
  );

  const formik = useFormik({
    initialValues: { otp: makeBlankOtp() },
    onSubmit: async (
      values,
      { setFieldError, setFieldValue, setSubmitting, setStatus },
    ) => {
      const code = ensureSlots(values.otp).replace(/\s/g, "");
      if (!isCompleteOtp(code)) {
        setFieldError("otp", t.errors.incomplete);
        return;
      }

      setStatus(undefined);
      setFieldError("otp", undefined);
      setSubmitting(true);
      try {
        const result = await verifyRegistrationOtp(code);
        setFieldValue("otp", makeBlankOtp());
        setStatus(undefined);
        setFieldError("otp", undefined);
        completeWith(result);
        window.requestAnimationFrame(() => focusInput(0));
      } catch (err) {
        const message = resolveOtpVerificationErrorMessage(err, t, shared);
        setFieldValue("otp", makeBlankOtp(), false);
        setFieldError("otp", undefined);
        if (message?.trim()) {
          showErrorMsg(message.trim());
        }
        window.requestAnimationFrame(() => focusInput(0));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    handleSubmit,
    setFieldValue,
    setFieldError,
    isSubmitting,
  } = formik;

  const focusInput = useCallback((index) => {
    const r = inputRefs.current[index];
    if (r?.current) r.current.focus();
  }, []);

  const applyOtpString = useCallback(
    (nextOtp) => {
      setFieldValue("otp", ensureSlots(nextOtp));
      setFieldError("otp", undefined);
    },
    [setFieldValue, setFieldError],
  );

  const handleDigitChange = useCallback(
    (index, e) => {
      const raw = e.target.value;
      const slots = ensureSlots(values.otp).split("");

      if (raw === "") {
        slots[index] = " ";
        applyOtpString(slots.join(""));
        return;
      }

      const digit = digitsOnly(raw).slice(-1);
      if (!digit) return;

      slots[index] = digit;
      applyOtpString(slots.join(""));

      if (index < OTP_LENGTH - 1) {
        window.requestAnimationFrame(() => focusInput(index + 1));
      }
    },
    [values.otp, applyOtpString, focusInput],
  );

  const handleDigitKeyDown = useCallback(
    (index, e) => {
      if (e.key !== "Backspace") return;
      const current = slotChar(values.otp, index);
      if (current === "" && index > 0) {
        e.preventDefault();
        const slots = ensureSlots(values.otp).split("");
        slots[index - 1] = " ";
        applyOtpString(slots.join(""));
        focusInput(index - 1);
      }
    },
    [values.otp, applyOtpString, focusInput],
  );

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pasted = digitsFromPaste(e.clipboardData.getData("text"));
      applyOtpString(buildOtpFromDigits(pasted));
      const len = Math.min(pasted.length, OTP_LENGTH);
      const nextFocus = Math.min(Math.max(0, len), OTP_LENGTH - 1);
      window.requestAnimationFrame(() => focusInput(nextFocus));
    },
    [applyOtpString, focusInput],
  );

  const handleResend = useCallback(async () => {
    if (resendDisabled) return;
    setIsResending(true);
    try {
      const data = await resendRegistrationOtp();
      persistCompanyPortalSession(data);
      const serverAllows = data?.can_resend_activation_code !== false;
      setCanResendActivationCode(serverAllows);
      if (serverAllows) {
        resetTimer();
      }
      applyOtpString(makeBlankOtp());
      focusInput(0);
      showSuccessMsg(t.resendSuccessMessage, true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 422) {
        setCanResendActivationCode(false);
      }
    } finally {
      setIsResending(false);
    }
  }, [
    resendDisabled,
    resetTimer,
    applyOtpString,
    focusInput,
    t.resendSuccessMessage,
  ]);

  const codeForValidation = ensureSlots(values.otp).replace(/\s/g, "");
  const isSubmitDisabled = !isCompleteOtp(codeForValidation) || isSubmitting;

  return (
    <div className="otp-verification-step w-100">
      <div>
        <h2 className="step-title">{t.title}</h2>
        <p className="step-description mb-0">{t.subtitle}</p>
      </div>

      <form
        className="otp-verification-step__form"
        onSubmit={handleSubmit}
        noValidate
        aria-busy={isSubmitting}
      >
        <div className="otp-verification-step__field">
          <h6 className="otp-verification-step__label mb-0 fweight-700">
            {t.otpLabel}
          </h6>

          <div
            className="otp-verification-step__inputs d-flex flex-row align-items-end flex-nowrap gap-2 gap-sm-3 gap-md-4"
            role="group"
            aria-label={t.otpLabel}
            dir="ltr"
          >
            {Array.from({ length: OTP_LENGTH }, (_, index) => (
              <React.Fragment key={index}>
                {index === OTP_LENGTH / 2 && (
                  <span
                    className="otp-verification-step__group-sep"
                    aria-hidden
                  >
                    <span className="otp-verification-step__group-sep-char">
                      —
                    </span>
                  </span>
                )}
                <span className="otp-verification-step__digit-wrap">
                  <input
                    ref={inputRefs.current[index]}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    name={`otp-${index}`}
                    maxLength={1}
                    size={1}
                    value={slotChar(values.otp, index)}
                    onChange={(e) => handleDigitChange(index, e)}
                    onKeyDown={(e) => handleDigitKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={`otp-verification-step__digit ${
                      slotChar(values.otp, index)
                        ? "otp-verification-step__digit--filled"
                        : ""
                    }${
                      errors.otp ? " otp-verification-step__digit--error" : ""
                    }`}
                    aria-invalid={errors.otp ? "true" : "false"}
                    aria-label={t.digitAriaLabel
                      .replace("{n}", String(index + 1))
                      .replace("{total}", String(OTP_LENGTH))}
                  />
                </span>
              </React.Fragment>
            ))}
          </div>

          {errors.otp && (
            <div
              className="otp-verification-step__error-slot"
              aria-live="polite"
            >
              <p className="otp-verification-step__error mb-0" role="alert">
                {errors.otp}
              </p>
            </div>
          )}
        </div>
        <div className="otp-verification-step__hint">
          <InfoIcon
            className="otp-verification-step__hint-icon"
            fontSize="small"
            aria-hidden
          />
          <span className="otp-verification-step__hint-text">
            <span>{t.didNotReceive}</span>{" "}
            <button
              type="button"
              className="otp-verification-step__resend"
              onClick={handleResend}
              disabled={resendDisabled}
            >
              {t.resend}
            </button>
            {showResendCooldown && (
              <>
                {" "}
                <span dir="ltr" className="otp-verification-step__countdown">
                  {countdownLabel}
                </span>
              </>
            )}
          </span>
        </div>

        {!canResendActivationCode && (
          <div
            className="otp-verification-step__resend-limit-warning"
            role="status"
            aria-live="polite"
          >
            {t.maxAttemptsReached}
          </div>
        )}

        <div className="otp-verification-step__actions d-flex flex-column flex-md-row gap-3 align-items-stretch">
          <div className="otp-verification-step__submit-wrap flex-grow-1">
            <UIButton
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              disabled={isSubmitDisabled}
              endIcon={<Check fontSize="small" />}
            >
              {t.actions.createAccount}
            </UIButton>
          </div>
        </div>
      </form>
    </div>
  );
};

OTPVerification.propTypes = {
  onNext: PropTypes.func,
  onSubmit: PropTypes.func,
};

OTPVerification.defaultProps = {
  onNext: () => {},
  onSubmit: null,
};

export default OTPVerification;
